import React, { useState, useCallback, useEffect } from "react";
import { Upload, X, CheckCircle } from "lucide-react";
import { useUpload } from "../context/UploadContext";
import { UPLOAD_URL } from "../utils/enum";
import { getEventIdFromPath } from "../utils/getEventId";
import { fetcheventsRequest } from "../store/slice/events/events.slice";
import { fetchPhotosRequest } from "../store/slice/images/images.slice";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

const PhotoUpload = ({ eventName }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedevent, setSelectedevent] = useState("");
  const { addUpload, markComplete } = useUpload();
  const [fullPath, setFullPath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("all");

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
  };

  const eventId = getEventIdFromPath(location.pathname);

  useEffect(() => {
    if (eventId) {
      dispatch(fetcheventsRequest());
      dispatch(fetchPhotosRequest(eventId));
    }
  }, [eventId]);
  useEffect(() => {
    if (eventName) {
      setSelectedevent(eventName);
    }
  }, [eventName, selectedevent]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith("image/")
    );

    handleFiles(files);
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleFiles(files);
    }
  };
  const handleFiles = async (files) => {
    const token = localStorage.getItem("token");
    const concurrencyLimit = 3;
    const queue = Array.from(files);
    const uploadingQueue = [...queue];
    let index = 0;
    let active = 0;
 let latestFullPath = ""; // 👈 local variable to store path
    // 1️⃣ Show all uploads in UI immediately
    queue.forEach((file) => addUpload(file.name));

    const uploadNext = async () => {
      if (index >= uploadingQueue.length) return;
      const file = uploadingQueue[index++];
      const formData = new FormData();
      formData.append("files", file);
      formData.append("neweventName", eventName);
      formData.append("category", category);
      active++;

      try {
        const response = await fetch(`${UPLOAD_URL}/create-event-and-upload`, {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const result = await response.json();
        if (response.ok) {
          markComplete(file.name); // ✅ Mark done individually
          latestFullPath = result.uploads?.[0].url.split("/")[0]; // 👈 update local variable
          setFullPath(latestFullPath); // 👈 update state
         
        } else {
          console.error("Upload failed for", file.name);
        }
      } catch (err) {
        console.error("Upload error:", err);
      } finally {
        active--;
        // 🔑 Condition: no more files left and nothing active
        if (index === uploadingQueue.length && active === 0) {
          dispatch(fetchPhotosRequest(eventId));
           if (latestFullPath) {
          //   setUploading(true);
            fetch("https://site.platformown.com/create-pkl", {
              method: "POST",
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
              },
              body: new URLSearchParams({ folder_key: latestFullPath }),
            })
              .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                
              })
              .catch((err) => {
                console.error("Background indexing failed:", err.message);
              });
          }
        }
        uploadNext(); // 👈 Kick off the next one
      }
    };

    // 2️⃣ Start up to 3 uploads
    for (let i = 0; i < concurrencyLimit && i < uploadingQueue.length; i++) {
      uploadNext();
    }
  };

  const removeFile = (id) => {
    setUploadedFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((f) => f.id !== id);
    });
  };

  const selectedeventChange = (e) => {
    setSelectedevent(e.target.value);
  };
  return (
    <div className="space-y-6">
      {/* event Selection */}
         <select
        className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        value={category}
        onChange={handleCategoryChange}
      >
        <option value="all">All</option>
        <option value="highlight">Highlight</option>
      </select>
      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
          isDragOver
            ? "border-blue-400 bg-blue-50"
            : "border-gray-300 hover:border-gray-400"
        }`}>
        <Upload className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Drop photos here or click to browse
        </h3>
        <p className="text-gray-500 mb-6">
          Supports JPG, PNG, GIF up to 10MB each
        </p>
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          id="file-upload"
        />
        <label
          htmlFor="file-upload"
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
          <Upload className="h-5 w-5 mr-2" />
          Select Photos
        </label>
      </div>

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            Uploaded Photos ({uploadedFiles.length})
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {uploadedFiles.map((file) => (
              <div key={file.id}>
                <img src={file.preview} width={100} />
                {file.status === "uploading" && (
                  <progress value={file.progress} max="100" />
                )}
                {file.status === "success" && <span>✅</span>}
                {file.status === "error" && <span>❌</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
